import React, { useState, useMemo, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Avatar,
  CircularProgress,
  Alert,
  FormHelperText,
  Divider,
  LinearProgress,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add,
  Edit,
  Delete,
  Close,
  Download,
  Visibility,
  PictureAsPdf,
  VideoLibrary,
  InsertDriveFile,
  OpenInNew,
} from "@mui/icons-material";
import {
  useMaterials,
  useCreateMaterial,
  useUpdateMaterial,
  useDeleteMaterial,
} from "../../hooks/queries/useAssignments";
import { useClassNames, useSubjectNames } from "../../hooks/queries/useCourses";
import departmentService from "../../services/departmentService";
import courseService from "../../services/courseService";
import assignmentService from "../../services/assignmentService";
import SearchBar from "../../components/navigation/SearchBar";
import Toast from "../../components/common/Toast";
import PermissionGate from "../../components/common/PermissionGate";
import ConfirmDialog from "../../components/forms/ConfirmDialog";
import FormFileUpload from "../../components/forms/FormFileUpload";
import studyMaterialDocumentService from "../../services/studyMaterialDocumentService";

// Allowed file types for upload: images, PDF, Word, Excel, and MP4 (meeting recordings)
const ALLOWED_FILE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "application/pdf",
  "application/msword", // .doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.ms-excel", // .xls
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "video/mp4", // .mp4
];
const ALLOWED_ACCEPT = ".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.mp4";
const FILE_TYPE_ERROR_MSG =
  "Only images, PDF, Word (DOC/DOCX), Excel (XLS/XLSX) and MP4 files are allowed.";

// Document repository types (separate from Materials)
const DOC_ALLOWED_MIME_TYPES = [
  "application/pdf",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "video/mp4",
  "video/mpeg",
  "video/quicktime",
  "video/webm",
  "text/plain",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];
const DOC_ALLOWED_ACCEPT = ".pdf,.csv,.xlsx,.xls,.doc,.docx,.ppt,.pptx,.mp4,.mov,.avi,.webm,.txt,.jpg,.jpeg,.png,.gif,.webp";

// Validation Schema for Create
const createValidationSchema = Yup.object({
  title: Yup.string()
    .required("Title is required")
    .min(3, "Title must be at least 3 characters")
    .max(200, "Title must not exceed 200 characters")
    .matches(/^[a-zA-Z0-9\s-]*$/, "Title can only contain letters, numbers, spaces and hyphens"),
  department: Yup.string().required("Department is required"),
  course: Yup.string().required("Course is required"),
  subject: Yup.string().required("Subject is required"),
  class: Yup.string().required("Class is required"),
  description: Yup.string().max(1000, "Description must not exceed 1000 characters"),
  files: Yup.array()
    .of(
      Yup.mixed()
        .test("fileSize", "Each file must be less than 100MB", (value) => {
          if (!value) return false;
          return value.size <= 100 * 1024 * 1024; // 100MB
        })
        .test("fileType", FILE_TYPE_ERROR_MSG, (value) => {
          if (!value) return false;
          return ALLOWED_FILE_TYPES.includes(value.type);
        })
    )
    .min(1, "At least one file is required")
    .max(10, "Maximum 10 files allowed"),
});

// Validation Schema for Edit
const editValidationSchema = Yup.object({
  title: Yup.string()
    .required("Title is required")
    .min(3, "Title must be at least 3 characters")
    .max(200, "Title must not exceed 200 characters")
    .matches(/^[a-zA-Z0-9\s-]*$/, "Title can only contain letters, numbers, spaces and hyphens"),
  department: Yup.string().required("Department is required"),
  course: Yup.string().required("Course is required"),
  subject: Yup.string().required("Subject is required"),
  class: Yup.string().required("Class is required"),
  description: Yup.string().max(1000, "Description must not exceed 1000 characters"),
  files: Yup.array()
    .of(
      Yup.mixed()
        .test("fileSize", "Each file must be less than 100MB", (value) => {
          if (!value) return true; // Optional field
          return value.size <= 100 * 1024 * 1024; // 100MB
        })
        .test("fileType", FILE_TYPE_ERROR_MSG, (value) => {
          if (!value) return true; // Optional field
          return ALLOWED_FILE_TYPES.includes(value.type);
        })
    )
    .max(10, "Maximum 10 files allowed")
    .nullable(),
});

const TeacherMaterials = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [materialToDelete, setMaterialToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState("success");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");

  // Document repository states (Document tab)
  const [docUploadOpen, setDocUploadOpen] = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const [docUploadProgress, setDocUploadProgress] = useState(0);
  const [docTitle, setDocTitle] = useState("");
  const [docDescription, setDocDescription] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [docSearch, setDocSearch] = useState("");
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(null);

  // Department, Course, Class, Subject dropdown states
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [courseLoading, setCourseLoading] = useState(false);
  const [classLoading, setClassLoading] = useState(false);
  const [subjectLoading, setSubjectLoading] = useState(false);

  // Fetch classes and subjects
  const {
    data: classesResponse,
    isLoading: classesLoading,
    refetch: refetchClasses,
  } = useClassNames();
  const {
    data: subjectsResponse,
    isLoading: subjectsLoading,
    refetch: refetchSubjects,
  } = useSubjectNames();

  // Create, update, and delete mutations
  const createMaterialMutation = useCreateMaterial();
  const updateMaterialMutation = useUpdateMaterial();
  const deleteMaterialMutation = useDeleteMaterial();

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      // Reset to first page when search changes
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Prepare query parameters
  const queryParams = useMemo(() => {
    return {
      page: paginationModel.page + 1, // API uses 1-based pagination
      limit: paginationModel.pageSize,
      search: debouncedSearchQuery || "",
      sort: "createdAt",
      order: "DESC",
    };
  }, [paginationModel, debouncedSearchQuery]);

  // Fetch materials from API
  const {
    data: materialsResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useMaterials(queryParams);

  const fetchDocuments = async () => {
    setDocumentsLoading(true);
    setDocumentsError(null);
    try {
      const resp = await studyMaterialDocumentService.list({ mine: "true" });
      const rows = resp?.data?.data || resp?.data?.documents || resp?.data || [];
      setDocuments(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setDocumentsError(
        e?.response?.data?.message || e?.message || "Failed to load documents"
      );
      setDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 1) {
      fetchDocuments();
    }
  }, [activeTab]);

  // Refetch materials when component mounts (page opens)
  useEffect(() => {
    refetch();
  }, []); // Empty dependency array means this runs once on mount

  // Extract classes and subjects from API response
  const classes = useMemo(() => {
    if (!classesResponse) return [];
    let data =
      classesResponse?.data?.data ||
      classesResponse?.data ||
      classesResponse ||
      [];
    if (data && typeof data === "object" && !Array.isArray(data)) {
      data = data.classes || data.items || data.names || [];
    }
    const result = Array.isArray(data) ? data : [];

    // Debug: Log the structure to understand the response
    if (process.env.NODE_ENV === 'development' && result.length > 0) {
      console.log('Classes response structure:', {
        firstItem: result[0],
        hasId: result[0]?.id !== undefined,
        hasName: result[0]?.name !== undefined,
        isString: typeof result[0] === 'string'
      });
    }

    return result;
  }, [classesResponse]);

  const subjects = useMemo(() => {
    if (!subjectsResponse) return [];
    let data =
      subjectsResponse?.data?.data ||
      subjectsResponse?.data ||
      subjectsResponse ||
      [];
    if (data && typeof data === "object" && !Array.isArray(data)) {
      data = data.subjects || data.items || data.names || [];
    }
    const result = Array.isArray(data) ? data : [];

    // Debug: Log the structure to understand the response
    if (process.env.NODE_ENV === 'development' && result.length > 0) {
      console.log('Subjects response structure:', {
        firstItem: result[0],
        hasId: result[0]?.id !== undefined,
        hasName: result[0]?.name !== undefined,
        isString: typeof result[0] === 'string'
      });
    }

    return result;
  }, [subjectsResponse]);

  // Refetch classes and subjects when dialogs open
  useEffect(() => {
    if (createDialogOpen || editDialogOpen) {
      refetchClasses();
      refetchSubjects();
    }
  }, [createDialogOpen, editDialogOpen, refetchClasses, refetchSubjects]);

  // Formik form for create
  const createFormik = useFormik({
    initialValues: {
      title: "",
      department: "",
      course: "",
      subject: "",
      class: "",
      description: "",
      files: [],
    },
    validationSchema: createValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        // Debug log to check form values
        if (process.env.NODE_ENV === 'development') {
          console.log('Create form values:', values);
          console.log('Department value:', values.department);
          console.log('Course value:', values.course);
          console.log('Subject value:', values.subject);
          console.log('Class value:', values.class);
        }

        // Ensure we have valid IDs - if the value is a string (name), find the ID
        let subjectId = values.subject;
        let classId = values.class;
        let departmentId = values.department;
        let courseId = values.course;

        // If subject is a name string, find the ID
        if (subjectId && typeof subjectId === 'string' && !/^\d+$/.test(subjectId)) {
          const foundSubject = subjects.find(s => (s.name || s) === subjectId);
          subjectId = foundSubject?.id || subjectId;
        }

        // If class is a name string, find the ID
        if (classId && typeof classId === 'string' && !/^\d+$/.test(classId)) {
          const foundClass = classes.find(c => (c.name || c) === classId);
          classId = foundClass?.id || classId;
        }

        if (departmentId && typeof departmentId === 'string' && !/^\d+$/.test(departmentId)) {
          const foundDepartment = departmentOptions.find(s => (s.name || s) === departmentId);
          departmentId = foundDepartment?.id || departmentId;
        }

        if (courseId && typeof courseId === 'string' && !/^\d+$/.test(courseId)) {
          const foundCourse = courseOptions.find(s => (s.name || s) === courseId);
          courseId = foundCourse?.id || courseId;
        }

        // Convert to string if it's a number
        subjectId = subjectId?.toString();
        classId = classId?.toString();
        departmentId = departmentId?.toString();
        courseId = courseId?.toString();

        const materialData = {
          name: values.title,
          details: values.description || "",
          departmentId: departmentId,
          courseId: courseId,
          subjectId: subjectId,
          classId: classId,
          files: values.files,
        };

        // Debug log
        if (process.env.NODE_ENV === 'development') {
          console.log('Creating material with data:', materialData);
        }

        await createMaterialMutation.mutateAsync(materialData);
        setToastMessage("Material uploaded successfully!");
        setToastSeverity("success");
        resetForm();
        setCreateDialogOpen(false);
        refetch();
      } catch (error) {
        setToastMessage(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to upload material"
        );
        setToastSeverity("error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Formik form for edit
  const editFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: selectedMaterial?.title || "",
      department: selectedMaterial?.originalData?.departmentId?.toString() || "",
      course: selectedMaterial?.originalData?.courseId?.toString() || "",
      subject: selectedMaterial?.originalData?.subjectId?.toString() ||
        subjects.find(s => s.name === selectedMaterial?.subject)?.id?.toString() ||
        selectedMaterial?.subject || "",
      class: selectedMaterial?.originalData?.classId?.toString() ||
        classes.find(c => c.name === selectedMaterial?.class)?.id?.toString() ||
        selectedMaterial?.class || "",
      description: selectedMaterial?.originalData?.details ||
        selectedMaterial?.originalData?.description || "",
      files: selectedMaterial?.originalData?.files || [],
    },
    validationSchema: editValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        // Ensure we have valid IDs - if the value is a string (name), find the ID
        let subjectId = values.subject;
        let classId = values.class;

        // If subject is a name string, find the ID
        if (subjectId && typeof subjectId === 'string' && !/^\d+$/.test(subjectId)) {
          const foundSubject = subjects.find(s => (s.name || s) === subjectId);
          subjectId = foundSubject?.id || subjectId;
        }

        // If class is a name string, find the ID
        if (classId && typeof classId === 'string' && !/^\d+$/.test(classId)) {
          const foundClass = classes.find(c => (c.name || c) === classId);
          classId = foundClass?.id || classId;
        }

        // Convert to string if it's a number
        subjectId = subjectId?.toString();
        classId = classId?.toString();

        const materialData = {
          name: values.title,
          details: values.description || "",
          departmentId: values.department,
          courseId: values.course,
          subjectId: subjectId,
          classId: classId,
          files: values.files,
        };

        // Debug log
        if (process.env.NODE_ENV === 'development') {
          console.log('Updating material with data:', materialData);
        }

        await updateMaterialMutation.mutateAsync({
          id: selectedMaterial.id,
          data: materialData,
        });
        // Auto immediate list hit right after update succeeds
        await refetch();
        setToastMessage("Material updated successfully!");
        setToastSeverity("success");
        resetForm();
        setSelectedMaterial(null);
        setEditDialogOpen(false);
      } catch (error) {
        setToastMessage(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update material"
        );
        setToastSeverity("error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleCreateTitleChange = (e) => {
    const value = e.target.value.slice(0, 200);
    createFormik.setFieldValue("title", value);
  };
  const handleEditTitleChange = (e) => {
    const value = e.target.value.slice(0, 200);
    editFormik.setFieldValue("title", value);
  };

  // Fetch departments on component mount
  useEffect(() => {
    let isMounted = true;
    const fetchDepartments = async () => {
      setDepartmentLoading(true);
      try {
        const response = await departmentService.getDepartmentNames();
        const departmentsData = response?.data?.data || response?.data || [];
        if (!isMounted) return;
        console.log('Departments fetched:', departmentsData);
        setDepartmentOptions(Array.isArray(departmentsData) ? departmentsData : []);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch departments:", error);
          setDepartmentOptions([]);
        }
      } finally {
        if (isMounted) {
          setDepartmentLoading(false);
        }
      }
    };
    fetchDepartments();
    return () => { isMounted = false; };
  }, []);

  // Fetch courses based on department for create form
  useEffect(() => {
    let isMounted = true;
    const fetchCourses = async () => {
      if (!createFormik.values.department) {
        if (isMounted) {
          setCourseOptions([]);
          setCourseLoading(false);
        }
        return;
      }
      setCourseLoading(true);
      try {
        const params = {
          id: createFormik.values.department,
          departmentId: createFormik.values.department,
        };
        const response = await assignmentService.getAssignmentCourseNames(params);
        const coursesDataRaw = response?.data?.data || response?.data || [];
        const coursesArray = Array.isArray(coursesDataRaw)
          ? coursesDataRaw
          : Array.isArray(coursesDataRaw?.courses)
            ? coursesDataRaw.courses
            : [];
        if (!isMounted) return;
        setCourseOptions(coursesArray);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch courses:", error);
          setCourseOptions([]);
        }
      } finally {
        if (isMounted) {
          setCourseLoading(false);
        }
      }
    };
    fetchCourses();
    return () => { isMounted = false; };
  }, [createFormik.values.department]);

  // Fetch classes based on course for create form
  useEffect(() => {
    let isMounted = true;
    const fetchClasses = async () => {
      if (!createFormik.values.course) {
        if (isMounted) {
          setClassOptions([]);
          setClassLoading(false);
        }
        return;
      }
      setClassLoading(true);
      try {
        const response = await courseService.getCourseClasses({
          id: createFormik.values.course,
        });
        const classesData = response?.data?.data || response?.data || [];
        if (!isMounted) return;
        setClassOptions(Array.isArray(classesData) ? classesData : []);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch classes:", error);
          setClassOptions([]);
        }
      } finally {
        if (isMounted) {
          setClassLoading(false);
        }
      }
    };
    fetchClasses();
    return () => { isMounted = false; };
  }, [createFormik.values.course]);

  // Fetch subjects based on class for create form
  useEffect(() => {
    let isMounted = true;
    const fetchSubjects = async () => {
      if (!createFormik.values.class) {
        if (isMounted) {
          setSubjectOptions([]);
          setSubjectLoading(false);
        }
        return;
      }
      setSubjectLoading(true);
      try {
        const response = await assignmentService.getAssignmentSubjectNames({
          id: createFormik.values.class,
        });
        const subjectsData = response?.data?.data || response?.data || [];
        if (!isMounted) return;
        setSubjectOptions(Array.isArray(subjectsData) ? subjectsData : []);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch subjects:", error);
          setSubjectOptions([]);
        }
      } finally {
        if (isMounted) {
          setSubjectLoading(false);
        }
      }
    };
    fetchSubjects();
    return () => { isMounted = false; };
  }, [createFormik.values.class]);

  // Fetch courses based on department for edit form
  useEffect(() => {
    let isMounted = true;
    const fetchCoursesForEdit = async () => {
      if (!editFormik.values.department) {
        if (isMounted) {
          setCourseOptions([]);
          setCourseLoading(false);
        }
        return;
      }
      setCourseLoading(true);
      try {
        const params = {
          id: editFormik.values.department,
          departmentId: editFormik.values.department,
        };
        const response = await assignmentService.getAssignmentCourseNames(params);
        const coursesDataRaw = response?.data?.data || response?.data || [];
        const coursesArray = Array.isArray(coursesDataRaw)
          ? coursesDataRaw
          : Array.isArray(coursesDataRaw?.courses)
            ? coursesDataRaw.courses
            : [];
        if (!isMounted) return;
        setCourseOptions(coursesArray);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch courses for edit:", error);
          setCourseOptions([]);
        }
      } finally {
        if (isMounted) {
          setCourseLoading(false);
        }
      }
    };
    fetchCoursesForEdit();
    return () => { isMounted = false; };
  }, [editFormik.values.department]);

  // Fetch classes based on course for edit form
  useEffect(() => {
    let isMounted = true;
    const fetchClassesForEdit = async () => {
      if (!editFormik.values.course) {
        if (isMounted) {
          setClassOptions([]);
          setClassLoading(false);
        }
        return;
      }
      setClassLoading(true);
      try {
        const response = await courseService.getCourseClasses({
          id: editFormik.values.course,
        });
        const classesData = response?.data?.data || response?.data || [];
        if (!isMounted) return;
        setClassOptions(Array.isArray(classesData) ? classesData : []);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch classes for edit:", error);
          setClassOptions([]);
        }
      } finally {
        if (isMounted) {
          setClassLoading(false);
        }
      }
    };
    fetchClassesForEdit();
    return () => { isMounted = false; };
  }, [editFormik.values.course]);

  // Fetch subjects based on class for edit form
  useEffect(() => {
    let isMounted = true;
    const fetchSubjectsForEdit = async () => {
      if (!editFormik.values.class) {
        if (isMounted) {
          setSubjectOptions([]);
          setSubjectLoading(false);
        }
        return;
      }
      setSubjectLoading(true);
      try {
        const response = await assignmentService.getAssignmentSubjectNames({
          id: editFormik.values.class,
        });
        const subjectsData = response?.data?.data || response?.data || [];
        if (!isMounted) return;
        setSubjectOptions(Array.isArray(subjectsData) ? subjectsData : []);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch subjects for edit:", error);
          setSubjectOptions([]);
        }
      } finally {
        if (isMounted) {
          setSubjectLoading(false);
        }
      }
    };
    fetchSubjectsForEdit();
    return () => { isMounted = false; };
  }, [editFormik.values.class]);

  // Handle cascading dropdown changes
  const handleCreateDepartmentChange = (value) => {
    console.log('Department changed to:', value);
    createFormik.setFieldValue('department', value);
    createFormik.setFieldValue('course', '');
    createFormik.setFieldValue('class', '');
    createFormik.setFieldValue('subject', '');
  };

  const handleCreateCourseChange = (value) => {
    createFormik.setFieldValue('course', value);
    createFormik.setFieldValue('class', '');
    createFormik.setFieldValue('subject', '');
  };

  const handleCreateClassChange = (value) => {
    createFormik.setFieldValue('class', value);
    createFormik.setFieldValue('subject', '');
  };

  const handleEditDepartmentChange = (value) => {
    editFormik.setFieldValue('department', value);
    editFormik.setFieldValue('course', '');
    editFormik.setFieldValue('class', '');
    editFormik.setFieldValue('subject', '');
  };

  const handleEditCourseChange = (value) => {
    editFormik.setFieldValue('course', value);
    editFormik.setFieldValue('class', '');
    editFormik.setFieldValue('subject', '');
  };

  const handleEditClassChange = (value) => {
    editFormik.setFieldValue('class', value);
    editFormik.setFieldValue('subject', '');
  };

  // Extract and transform materials data
  const materials = useMemo(() => {
    if (!materialsResponse) return [];

    // Handle different response structures
    const materialsArray =
      materialsResponse?.data?.data?.materials ||
      materialsResponse?.data?.materials ||
      materialsResponse?.data?.data ||
      (Array.isArray(materialsResponse?.data) ? materialsResponse.data : []) ||
      (Array.isArray(materialsResponse) ? materialsResponse : []);

    if (!Array.isArray(materialsArray) || materialsArray.length === 0) {
      return [];
    }

    // Transform to match DataGrid format
    return materialsArray.map((material) => {
      // Format uploaded date
      let formattedDate = "";
      if (material.createdAt || material.uploadedAt) {
        const date = new Date(material.createdAt || material.uploadedAt);
        formattedDate = date.toISOString().split("T")[0]; // YYYY-MM-DD format
      }

      // Determine file type from files array or file extension
      let fileType = "file";
      let totalFileSize = 0;

      if (material.files && Array.isArray(material.files) && material.files.length > 0) {
        // Get type from first file
        const firstFile = material.files[0];
        if (firstFile.type) {
          if (firstFile.type.includes("pdf")) fileType = "pdf";
          else if (firstFile.type.includes("video")) fileType = "video";
          else if (firstFile.type.includes("image")) fileType = "image";
        } else if (firstFile.name) {
          const extension = firstFile.name.split(".").pop()?.toLowerCase();
          if (extension === "pdf") fileType = "pdf";
          else if (["mp4", "avi", "mov", "wmv", "flv"].includes(extension))
            fileType = "video";
          else if (["jpg", "jpeg", "png", "gif"].includes(extension))
            fileType = "image";
        }

        // Calculate total file size
        totalFileSize = material.files.reduce((sum, file) => sum + (file.size || 0), 0);
      } else if (material.fileName || material.file) {
        // Fallback to old structure
        const fileName = material.fileName || material.file || "";
        const extension = fileName.split(".").pop()?.toLowerCase();
        if (extension === "pdf") fileType = "pdf";
        else if (["mp4", "avi", "mov", "wmv", "flv"].includes(extension))
          fileType = "video";
        else if (["jpg", "jpeg", "png", "gif"].includes(extension))
          fileType = "image";
      }

      // Format file size
      let fileSize = "0 B";
      if (totalFileSize > 0) {
        const bytes = totalFileSize;
        if (bytes < 1024) fileSize = `${bytes} B`;
        else if (bytes < 1024 * 1024)
          fileSize = `${(bytes / 1024).toFixed(1)} KB`;
        else
          fileSize = `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
      } else if (material.size) {
        const bytes = material.size;
        if (bytes < 1024) fileSize = `${bytes} B`;
        else if (bytes < 1024 * 1024)
          fileSize = `${(bytes / 1024).toFixed(1)} KB`;
        else
          fileSize = `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
      }

      return {
        id: material.id,
        title: material.title || material.name || "",
        subject: material.Subject?.name || material.subject || "",
        class: material.classDisplayName || (material.Class ? [material.Class.name, material.Class.section].filter(Boolean).join(" - ") : (material.class || material.className || "")),
        type: fileType,
        size: fileSize,
        uploadedAt: formattedDate,
        downloads: material.downloads || material.downloadCount || 0,
        originalData: material,
      };
    });
  }, [materialsResponse]);

  // Get pagination info from response
  const paginationInfo = useMemo(() => {
    if (!materialsResponse) return { total: 0 };
    return {
      total:
        materialsResponse?.data?.pagination?.totalItems ||
        materialsResponse?.data?.data?.pagination?.totalItems ||
        materialsResponse?.data?.total ||
        materialsResponse?.data?.data?.total ||
        materialsResponse?.total ||
        materials.length,
    };
  }, [materialsResponse, materials.length]);

  // Filter materials based on active tab
  const filteredMaterials = useMemo(() => {
    let list = materials;
    // NOTE: Document repository is a separate backend; keep Materials tab as-is.

    if (subjectFilter !== "all") {
      list = list.filter((m) => m.subject === subjectFilter);
    }
    if (classFilter !== "all") {
      list = list.filter((m) => m.class === classFilter);
    }

    return list;
  }, [materials, activeTab, subjectFilter, classFilter]);

  const filteredDocuments = useMemo(() => {
    const q = (docSearch || "").trim().toLowerCase();
    const list = Array.isArray(documents) ? documents : [];
    if (!q) return list;
    return list.filter((d) => {
      const fileName = String(d.fileName || "").toLowerCase();
      const title = String(d.title || "").toLowerCase();
      const uploader = String(d.uploadedByUser?.fullName || d.uploadedByUser?.email || "").toLowerCase();
      return fileName.includes(q) || title.includes(q) || uploader.includes(q);
    });
  }, [documents, docSearch]);

  // Handle edit button click
  const handleEditClick = (material) => {
    setSelectedMaterial(material);

    const fetchEditOptions = async () => {
      try {
        const departmentId = material?.originalData?.departmentId ?? material?.departmentId;
        const courseId = material?.originalData?.courseId ?? material?.courseId;
        const classId = material?.originalData?.classId ?? material?.classId;

        if (departmentId) {
          setCourseLoading(true);
          const courseParams = {
            id: departmentId,
            departmentId: departmentId,
          };
          const courseResponse = await assignmentService.getAssignmentCourseNames(
            courseParams
          );
          const coursesDataRaw = courseResponse?.data?.data || courseResponse?.data || [];
          const coursesArray = Array.isArray(coursesDataRaw)
            ? coursesDataRaw
            : Array.isArray(coursesDataRaw?.courses)
              ? coursesDataRaw.courses
              : [];
          setCourseOptions(coursesArray);
        }

        if (courseId) {
          setClassLoading(true);
          const classResponse = await courseService.getCourseClasses({
            id: courseId,
          });
          const classesData = classResponse?.data?.data || classResponse?.data || [];
          setClassOptions(Array.isArray(classesData) ? classesData : []);
        }

        if (classId) {
          setSubjectLoading(true);
          const subjectResponse = await assignmentService.getAssignmentSubjectNames({
            id: classId,
          });
          const subjectsData =
            subjectResponse?.data?.data || subjectResponse?.data || [];
          setSubjectOptions(Array.isArray(subjectsData) ? subjectsData : []);
        }
      } catch (error) {
        console.error("Failed to fetch edit dropdown options:", error);
      } finally {
        setCourseLoading(false);
        setClassLoading(false);
        setSubjectLoading(false);
      }
    };

    fetchEditOptions();
    setEditDialogOpen(true);
  };

  // Handle delete button click
  const handleDeleteClick = (material) => {
    setMaterialToDelete(material);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!materialToDelete) return;

    try {
      await deleteMaterialMutation.mutateAsync(materialToDelete.id);
      setToastMessage("Material deleted successfully!");
      setToastSeverity("success");
      setDeleteDialogOpen(false);
      setMaterialToDelete(null);
      refetch();
    } catch (error) {
      setToastMessage(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete material"
      );
      setToastSeverity("error");
    }
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    createFormik.resetForm();
    setCreateDialogOpen(false);
  };

  // Handle edit dialog close
  const handleCloseEditDialog = () => {
    editFormik.resetForm();
    setSelectedMaterial(null);
    setEditDialogOpen(false);
  };

  const getFileIcon = (type) => {
    switch (type) {
      case "pdf":
        return <PictureAsPdf sx={{ color: "#e53935" }} />;
      case "video":
        return <VideoLibrary sx={{ color: "#1976d2" }} />;
      default:
        return <InsertDriveFile sx={{ color: "#757575" }} />;
    }
  };

  const columns = [
    {
      field: "title",
      headerName: "Material",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ bgcolor: "grey.100", width: 36, height: 36 }}>
            {getFileIcon(params.row.type)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.size}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { field: "subject", headerName: "Subject", width: 300, minWidth: 260 },
    {
      field: "class",
      headerName: "Class",
      width: 200,
      minWidth: 160,
      renderCell: (params) => (
        <Box component="span" sx={{ pl: 2 }}>
          {params.value ?? ""}
        </Box>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      width: 80,
      renderCell: (params) => (
        <Chip label={params.value.toUpperCase()} size="small" />
      ),
    },
    { field: "uploadedAt", headerName: "Uploaded", width: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => {
              setSelectedMaterial(params.row);
              setViewDialogOpen(true);
            }}
            title="View Material"
          >
            <Visibility />
          </IconButton>
          <PermissionGate permission="material.update">
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.row)}
              title="Edit Material"
            >
              <Edit />
            </IconButton>
          </PermissionGate>
          <PermissionGate permission="material.delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row)}
              title="Delete Material"
            >
              <Delete />
            </IconButton>
          </PermissionGate>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Study Materials
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload and manage study materials
          </Typography>
        </Box>
        {activeTab === 0 ? (
          <PermissionGate permission="material.create">
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Upload material
            </Button>
          </PermissionGate>
        ) : (
          <PermissionGate permission="material.upload">
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setDocUploadOpen(true)}
            >
              Upload document
            </Button>
          </PermissionGate>
        )}
      </Box>

      <Card>
        <CardContent>
          {activeTab === 0 ? (
            <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <Box sx={{ width: 350 }}>
                <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search materials..."
                  onClear={() => setSearchQuery("")}
                />
              </Box>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={subjectFilter}
                  label="Subject"
                  onChange={(e) => setSubjectFilter(e.target.value)}
                >
                  <MenuItem value="all">All Subjects</MenuItem>
                  {subjects.map((s) => (
                    <MenuItem key={s.id || s.name} value={s.name}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Class</InputLabel>
                <Select
                  value={classFilter}
                  label="Class"
                  onChange={(e) => setClassFilter(e.target.value)}
                >
                  <MenuItem value="all">All Classes</MenuItem>
                  {classes.map((c) => {
                    const displayValue = [c.name ?? c.className ?? c.label, c.section].filter(Boolean).join(" - ");
                    return (
                      <MenuItem key={c.id || c.name} value={displayValue}>
                        {displayValue}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {(subjectFilter !== "all" || classFilter !== "all") && (
                <Button
                  size="small"
                  onClick={() => {
                    setSubjectFilter("all");
                    setClassFilter("all");
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          ) : (
            <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <Box sx={{ width: 350 }}>
                <SearchBar
                  value={docSearch}
                  onChange={setDocSearch}
                  placeholder="Search documents..."
                  onClear={() => setDocSearch("")}
                />
              </Box>
              <Button size="small" onClick={fetchDocuments} disabled={documentsLoading}>
                Refresh
              </Button>
            </Box>
          )}

          <Tabs
            value={activeTab}
            onChange={(e, v) => {
              setActiveTab(v);
              // Reset to first page when tab changes
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            sx={{ mb: 2 }}
          >
            <Tab label="All Materials" />
            <Tab label="My Documents" />
          </Tabs>

          {activeTab === 0 && isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                Error loading materials
              </Typography>
              <Typography variant="caption" component="div">
                {error?.response?.data?.message ||
                  error?.message ||
                  "Failed to load materials. Please try again."}
              </Typography>
            </Alert>
          )}

          {activeTab === 0 && isLoading && filteredMaterials.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
              }}
            >
              <CircularProgress />
            </Box>
          ) : activeTab === 0 ? (
            <DataGrid
              rows={filteredMaterials}
              columns={columns}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              paginationMode="server"
              rowCount={paginationInfo.total}
              loading={isLoading}
              sx={{ border: "none" }}
            />
          ) : (
            <>
              {!!documentsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {documentsError}
                </Alert>
              )}
              {documentsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 300,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <DataGrid
                  rows={filteredDocuments.map((d) => ({
                    id: d.id,
                    fileName: d.fileName,
                    title: d.title,
                    uploadedBy: d.uploadedByUser?.fullName || d.uploadedByUser?.email || "",
                    uploadDate: d.createdAt ? new Date(d.createdAt).toISOString().split("T")[0] : "",
                    fileType: d.fileType,
                    _raw: d,
                  }))}
                  columns={[
                    { field: "fileName", headerName: "File Name", flex: 1, minWidth: 240 },
                    { field: "title", headerName: "Title", flex: 1, minWidth: 220 },
                    { field: "uploadedBy", headerName: "Uploaded By", width: 200, minWidth: 180 },
                    { field: "uploadDate", headerName: "Upload Date", width: 120 },
                    {
                      field: "fileType",
                      headerName: "File Type",
                      width: 180,
                      renderCell: (params) => (
                        <Chip
                          label={String(params.value || "").split("/").pop()?.toUpperCase() || "FILE"}
                          size="small"
                        />
                      ),
                    },
                    {
                      field: "actions",
                      headerName: "Actions",
                      width: 180,
                      sortable: false,
                      renderCell: (params) => {
                        const row = params.row?._raw;
                        const downloadPath = studyMaterialDocumentService.downloadUrl(row?.id);
                        const viewUrl = row?.fileUrl || downloadPath;
                        return (
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <IconButton
                              size="small"
                              title="View"
                              component="a"
                              href={viewUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Visibility />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Download"
                              component="a"
                              href={downloadPath}
                            >
                              <Download />
                            </IconButton>
                            <PermissionGate permission="material.upload">
                              <IconButton
                                size="small"
                                color="error"
                                title="Delete"
                                onClick={async () => {
                                  try {
                                    await studyMaterialDocumentService.delete(row?.id);
                                    setToastMessage("Document deleted successfully");
                                    setToastSeverity("success");
                                    fetchDocuments();
                                  } catch (e) {
                                    setToastMessage(
                                      e?.response?.data?.message ||
                                        e?.message ||
                                        "Failed to delete document"
                                    );
                                    setToastSeverity("error");
                                  }
                                }}
                              >
                                <Delete />
                              </IconButton>
                            </PermissionGate>
                          </Box>
                        );
                      },
                    },
                  ]}
                  autoHeight
                  disableRowSelectionOnClick
                  pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                  initialState={{
                    pagination: { paginationModel: { page: 0, pageSize: 10 } },
                  }}
                  sx={{ border: "none" }}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Document Upload Dialog (Document tab) */}
      <Dialog
        open={docUploadOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setDocUploadOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Upload Document
            <IconButton
              onClick={() => {
                setDocUploadOpen(false);
              }}
              disabled={docUploading}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Title"
                value={docTitle}
                onChange={(e) => setDocTitle(e.target.value.slice(0, 200))}
                disabled={docUploading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={docDescription}
                onChange={(e) => setDocDescription(e.target.value)}
                disabled={docUploading}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" disabled={docUploading}>
                Choose file
                <input
                  type="file"
                  hidden
                  accept={DOC_ALLOWED_ACCEPT}
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    if (f && f.type && !DOC_ALLOWED_MIME_TYPES.includes(f.type)) {
                      setToastMessage("Unsupported file type");
                      setToastSeverity("error");
                      setDocFile(null);
                      return;
                    }
                    setDocFile(f);
                  }}
                />
              </Button>
              <Typography variant="caption" sx={{ ml: 1 }}>
                {docFile ? docFile.name : "No file selected"}
              </Typography>
              <FormHelperText sx={{ mt: 0.5 }}>
                Allowed: PDF, CSV, DOC/DOCX, XLS/XLSX, PPT/PPTX, MP4, TXT, Images (max 100MB)
              </FormHelperText>
            </Grid>
            {docUploading && (
              <Grid item xs={12}>
                <LinearProgress variant="determinate" value={docUploadProgress} />
                <Typography variant="caption" color="text.secondary">
                  Uploading… {Math.round(docUploadProgress)}%
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDocUploadOpen(false)}
            disabled={docUploading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={docUploading || !docTitle.trim() || !docFile}
            onClick={async () => {
              try {
                setDocUploading(true);
                setDocUploadProgress(0);
                await studyMaterialDocumentService.upload({
                  title: docTitle.trim(),
                  description: docDescription,
                  file: docFile,
                  onUploadProgress: (evt) => {
                    const total = evt.total || 0;
                    if (!total) return;
                    const pct = (evt.loaded / total) * 100;
                    setDocUploadProgress(pct);
                  },
                });
                setToastMessage("Document uploaded successfully!");
                setToastSeverity("success");
                setDocTitle("");
                setDocDescription("");
                setDocFile(null);
                setDocUploadOpen(false);
                fetchDocuments();
              } catch (e) {
                setToastMessage(
                  e?.response?.data?.message ||
                    e?.message ||
                    "Failed to upload document"
                );
                setToastSeverity("error");
              } finally {
                setDocUploading(false);
                setDocUploadProgress(0);
              }
            }}
          >
            {docUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleCloseDialog();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Upload Material
            <IconButton onClick={handleCloseDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Box component="form" onSubmit={createFormik.handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  required
                  name="title"
                  value={createFormik.values.title}
                  onChange={handleCreateTitleChange}
                  onBlur={createFormik.handleBlur}
                  error={
                    createFormik.touched.title &&
                    Boolean(createFormik.errors.title)
                  }
                  helperText={
                    createFormik.touched.title && createFormik.errors.title
                  }
                  placeholder="Letters, numbers and spaces, 3–200 characters"
                  inputProps={{ maxLength: 200 }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  required
                  error={
                    createFormik.touched.department &&
                    Boolean(createFormik.errors.department)
                  }
                >
                  <InputLabel>Department</InputLabel>
                  <Select
                    name="department"
                    value={createFormik.values.department}
                    label="Department"
                    onChange={(e) => handleCreateDepartmentChange(e.target.value)}
                    onBlur={createFormik.handleBlur}
                    disabled={departmentLoading}
                  >
                    {departmentLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      departmentOptions.map((dept) => (
                        <MenuItem key={dept.id ?? dept.departmentId ?? dept.name} value={dept.id ?? dept.departmentId ?? dept.value}>
                          {dept.name ?? dept.departmentName ?? dept.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {createFormik.touched.department &&
                    createFormik.errors.department && (
                      <FormHelperText>
                        {createFormik.errors.department}
                      </FormHelperText>
                    )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  required
                  error={
                    createFormik.touched.course &&
                    Boolean(createFormik.errors.course)
                  }
                >
                  <InputLabel>Course</InputLabel>
                  <Select
                    name="course"
                    value={createFormik.values.course}
                    label="Course"
                    onChange={(e) => handleCreateCourseChange(e.target.value)}
                    onBlur={createFormik.handleBlur}
                    disabled={courseLoading || !createFormik.values.department}
                  >
                    {courseLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      courseOptions.map((course) => (
                        <MenuItem key={course.id ?? course.courseId ?? course.name} value={course.id ?? course.courseId ?? course.value}>
                          {course.name ?? course.courseName ?? course.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {createFormik.touched.course &&
                    createFormik.errors.course && (
                      <FormHelperText>
                        {createFormik.errors.course}
                      </FormHelperText>
                    )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  required
                  error={
                    createFormik.touched.class &&
                    Boolean(createFormik.errors.class)
                  }
                >
                  <InputLabel>Class</InputLabel>
                  <Select
                    name="class"
                    value={createFormik.values.class}
                    label="Class"
                    onChange={(e) => handleCreateClassChange(e.target.value)}
                    onBlur={createFormik.handleBlur}
                    disabled={classLoading || !createFormik.values.course}
                  >
                    {classLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      classOptions.map((cls) => (
                        <MenuItem key={cls.id ?? cls.classId ?? cls.name} value={cls.id ?? cls.classId ?? cls.value}>
                          {[cls.name ?? cls.className ?? cls.label, cls.section].filter(Boolean).join(" - ")}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {createFormik.touched.class && createFormik.errors.class && (
                    <FormHelperText>{createFormik.errors.class}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6} sx={{ pl: 2 }}>
                <FormControl
                  fullWidth
                  required
                  error={
                    createFormik.touched.subject &&
                    Boolean(createFormik.errors.subject)
                  }
                >
                  <InputLabel>Subject</InputLabel>
                  <Select
                    name="subject"
                    value={createFormik.values.subject}
                    label="Subject"
                    onChange={createFormik.handleChange}
                    onBlur={createFormik.handleBlur}
                    disabled={subjectLoading || !createFormik.values.class}
                  >
                    {subjectLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      subjectOptions.map((subject) => (
                        <MenuItem key={subject.id ?? subject.subjectId ?? subject.name} value={subject.id ?? subject.subjectId ?? subject.value}>
                          {subject.name ?? subject.subjectName ?? subject.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {createFormik.touched.subject &&
                    createFormik.errors.subject && (
                      <FormHelperText>
                        {createFormik.errors.subject}
                      </FormHelperText>
                    )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  name="description"
                  value={createFormik.values.description}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  error={
                    createFormik.touched.description &&
                    Boolean(createFormik.errors.description)
                  }
                  helperText={
                    createFormik.touched.description &&
                      createFormik.errors.description
                      ? createFormik.errors.description
                      : `${createFormik.values.description.length}/1000 characters`
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormFileUpload
                  name="files"
                  value={createFormik.values.files}
                  onChange={(files) => {
                    createFormik.setFieldValue("files", files);
                    createFormik.setFieldTouched("files", true);
                    // Re-validate files after state commits
                    setTimeout(() => createFormik.validateField("files"), 0);
                  }}
                  multiple={true}
                  label="Upload Files *"
                  maxSize={100}
                  accept={ALLOWED_ACCEPT}
                />
                <FormHelperText sx={{ mt: 0.5, ml: 1.75 }}>
                  Allowed: PNG, JPEG, PDF, DOCX (max 100MB)
                </FormHelperText>
                {createFormik.touched.files && createFormik.errors.files && createFormik.values.files.length === 0 && (
                  <FormHelperText error sx={{ mt: 0.5, ml: 1.75 }}>
                    {createFormik.errors.files}
                  </FormHelperText>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDialog}
              disabled={createFormik.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                createFormik.isSubmitting ||
                createMaterialMutation.isPending ||
                !createFormik.isValid ||
                createFormik.values.files.length === 0
              }
            >
              {createFormik.isSubmitting || createMaterialMutation.isPending
                ? "Uploading..."
                : "Upload"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleCloseEditDialog();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Edit Material
            <IconButton onClick={handleCloseEditDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Box component="form" onSubmit={editFormik.handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  required
                  name="title"
                  value={editFormik.values.title}
                  onChange={handleEditTitleChange}
                  onBlur={editFormik.handleBlur}
                  error={
                    editFormik.touched.title && Boolean(editFormik.errors.title)
                  }
                  helperText={
                    editFormik.touched.title && editFormik.errors.title
                  }
                  placeholder="Letters, numbers and spaces, 3–200 characters"
                  inputProps={{ maxLength: 200 }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  required
                  error={
                    editFormik.touched.department &&
                    Boolean(editFormik.errors.department)
                  }
                >
                  <InputLabel>Department</InputLabel>
                  <Select
                    name="department"
                    value={editFormik.values.department}
                    label="Department"
                    onChange={(e) => handleEditDepartmentChange(e.target.value)}
                    onBlur={editFormik.handleBlur}
                    disabled={departmentLoading}
                  >
                    {departmentLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      departmentOptions.map((dept) => (
                        <MenuItem key={dept.id ?? dept.departmentId ?? dept.name} value={dept.id ?? dept.departmentId ?? dept.value}>
                          {dept.name ?? dept.departmentName ?? dept.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {editFormik.touched.department &&
                    editFormik.errors.department && (
                      <FormHelperText>
                        {editFormik.errors.department}
                      </FormHelperText>
                    )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  required
                  error={
                    editFormik.touched.course &&
                    Boolean(editFormik.errors.course)
                  }
                >
                  <InputLabel>Course</InputLabel>
                  <Select
                    name="course"
                    value={editFormik.values.course}
                    label="Course"
                    onChange={(e) => handleEditCourseChange(e.target.value)}
                    onBlur={editFormik.handleBlur}
                    disabled={courseLoading || !editFormik.values.department}
                  >
                    {courseLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      courseOptions.map((course) => (
                        <MenuItem key={course.id ?? course.courseId ?? course.name} value={course.id ?? course.courseId ?? course.value}>
                          {course.name ?? course.courseName ?? course.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {editFormik.touched.course &&
                    editFormik.errors.course && (
                      <FormHelperText>
                        {editFormik.errors.course}
                      </FormHelperText>
                    )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  required
                  error={
                    editFormik.touched.class && Boolean(editFormik.errors.class)
                  }
                >
                  <InputLabel>Class</InputLabel>
                  <Select
                    name="class"
                    value={editFormik.values.class}
                    label="Class"
                    onChange={(e) => handleEditClassChange(e.target.value)}
                    onBlur={editFormik.handleBlur}
                    disabled={classLoading || !editFormik.values.course}
                  >
                    {classLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      classOptions.map((cls) => (
                        <MenuItem key={cls.id ?? cls.classId ?? cls.name} value={cls.id ?? cls.classId ?? cls.value}>
                          {[cls.name ?? cls.className ?? cls.label, cls.section].filter(Boolean).join(" - ")}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {editFormik.touched.class && editFormik.errors.class && (
                    <FormHelperText>{editFormik.errors.class}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6} sx={{ pl: 2 }}>
                <FormControl
                  fullWidth
                  required
                  error={
                    editFormik.touched.subject &&
                    Boolean(editFormik.errors.subject)
                  }
                >
                  <InputLabel>Subject</InputLabel>
                  <Select
                    name="subject"
                    value={editFormik.values.subject}
                    label="Subject"
                    onChange={editFormik.handleChange}
                    onBlur={editFormik.handleBlur}
                    disabled={subjectLoading || !editFormik.values.class}
                  >
                    {subjectLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      subjectOptions.map((subject) => (
                        <MenuItem key={subject.id ?? subject.subjectId ?? subject.name} value={subject.id ?? subject.subjectId ?? subject.value}>
                          {subject.name ?? subject.subjectName ?? subject.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {editFormik.touched.subject && editFormik.errors.subject && (
                    <FormHelperText>{editFormik.errors.subject}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  name="description"
                  value={editFormik.values.description}
                  onChange={editFormik.handleChange}
                  onBlur={editFormik.handleBlur}
                  error={
                    editFormik.touched.description &&
                    Boolean(editFormik.errors.description)
                  }
                  helperText={
                    editFormik.touched.description &&
                      editFormik.errors.description
                      ? editFormik.errors.description
                      : `${editFormik.values.description.length}/1000 characters`
                  }
                />
              </Grid>
              <Grid item xs={12}>
                {/* Current uploaded file(s) - show when editing */}
                {!editFormik.values.file && selectedMaterial?.originalData && (
                  (() => {
                    const files = selectedMaterial.originalData.files;
                    const fileList = Array.isArray(files) && files.length > 0
                      ? files
                      : selectedMaterial.originalData.file
                        ? [selectedMaterial.originalData.file]
                        : [];
                    if (fileList.length === 0) return null;
                    return (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Current uploaded file(s)
                        </Typography>
                        {fileList.map((file, index) => (
                          <Box
                            key={index}
                            sx={{
                              p: 1.5,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 1,
                            }}
                          >
                            <Avatar sx={{ bgcolor: "grey.100", width: 40, height: 40 }}>
                              {getFileIcon(
                                file.type?.includes?.("pdf") ? "pdf" : "file"
                              )}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={500} noWrap>
                                Material {index + 1}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {file.size ? `${(file.size / 1024).toFixed(2)} KB` : ""}
                                {file.size && file.type ? " • " : ""}
                                {file.type || ""}
                              </Typography>
                            </Box>
                            {file.url && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<OpenInNew />}
                                component="a"
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </Button>
                            )}
                          </Box>
                        ))}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                          Upload a new file below to replace, or leave empty to keep current.
                        </Typography>
                      </Box>
                    );
                  })()
                )}
                <FormFileUpload
                  name="edit-files"
                  value={editFormik.values.files}
                  onChange={(files) => {
                    editFormik.setFieldValue("files", files);
                    editFormik.setFieldTouched("files", true);
                    setTimeout(() => editFormik.validateField("files"), 0);
                  }}
                  multiple={true}
                  label="Add files (optional)"
                  maxSize={100}
                  accept={ALLOWED_ACCEPT}
                />
                <FormHelperText sx={{ mt: 0.5, ml: 1.75 }}>
                  Allowed: PNG, JPEG, PDF, DOCX (max 100MB)
                </FormHelperText>
                {editFormik.touched.files && editFormik.errors.files && (
                  <FormHelperText error sx={{ mt: 0.5, ml: 1.75 }}>
                    {editFormik.errors.files}
                  </FormHelperText>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseEditDialog}
              disabled={editFormik.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                editFormik.isSubmitting ||
                updateMaterialMutation.isPending ||
                !editFormik.isValid ||
                Object.keys(editFormik.errors).length > 0
              }
            >
              {editFormik.isSubmitting || updateMaterialMutation.isPending
                ? "Updating..."
                : "Update"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Material"
        message={
          <>
            Are you sure you want to delete the material{" "}
            <strong>"{materialToDelete?.title}"</strong>? This action cannot be
            undone.
          </>
        }
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        loading={deleteMaterialMutation.isPending}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="error"
      />

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setViewDialogOpen(false);
          setSelectedMaterial(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Material Details</Typography>
            <IconButton
              onClick={() => {
                setViewDialogOpen(false);
                setSelectedMaterial(null);
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedMaterial?.originalData && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Basic Information
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Name
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedMaterial.originalData.name || selectedMaterial.title}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Details
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {selectedMaterial.originalData.details || selectedMaterial.originalData.description || "N/A"}
                  </Typography>
                </Grid>
                {/* Material ID - commented out
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Material ID
                  </Typography>
                  <Typography variant="body1">{selectedMaterial.originalData.id || "N/A"}</Typography>
                </Grid>
                */}
                {/* UUID - commented out
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    UUID
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {selectedMaterial.originalData.uuid || "N/A"}
                  </Typography>
                </Grid>
                */}

                {/* Subject Information */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Subject Information
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Subject
                  </Typography>
                  <Chip
                    label={selectedMaterial.originalData.Subject?.name || selectedMaterial.subject || "N/A"}
                    size="small"
                    color="primary"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Subject Code
                  </Typography>
                  <Typography variant="body1">
                    {selectedMaterial.originalData.Subject?.code || "N/A"}
                  </Typography>
                </Grid>
                {/* Subject ID - commented out
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Subject ID
                  </Typography>
                  <Typography variant="body1">
                    {selectedMaterial.originalData.subjectId || "N/A"}
                  </Typography>
                </Grid>
                */}

                {/* Class Information */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Class Information
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Class
                  </Typography>
                  <Chip
                    label={selectedMaterial.originalData.Class?.name || selectedMaterial.class || "N/A"}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Section
                  </Typography>
                  <Typography variant="body1">
                    {selectedMaterial.originalData.Class?.section || "N/A"}
                  </Typography>
                </Grid>
                {/* Class ID - commented out
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Class ID
                  </Typography>
                  <Typography variant="body1">
                    {selectedMaterial.originalData.classId || "N/A"}
                  </Typography>
                </Grid>
                */}

                {/* Teacher Information - commented out
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Teacher Information
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Teacher Name
                  </Typography>
                  <Typography variant="body1">
                    {selectedMaterial.originalData.Teacher?.User?.fullName || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Teacher Email
                  </Typography>
                  <Typography variant="body1">
                    {selectedMaterial.originalData.Teacher?.User?.email || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Teacher ID
                  </Typography>
                  <Typography variant="body1">
                    {selectedMaterial.originalData.teacherId || "N/A"}
                  </Typography>
                </Grid>
                */}

                {/* Department Information */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Department Information
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Department
                  </Typography>
                  <Typography variant="body1">
                    {selectedMaterial.originalData.Department?.name ||
                      selectedMaterial.originalData.department?.name ||
                      "N/A"}
                  </Typography>
                </Grid>
                {/* Department ID - commented out
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Department ID
                  </Typography>
                  <Typography variant="body1">
                    {selectedMaterial.originalData.departmentId ||
                      selectedMaterial.originalData.department?.id ||
                      "N/A"}
                  </Typography>
                </Grid>
                */}
                {/* Institute ID - commented out
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Institute ID
                  </Typography>
                  <Typography variant="body1">
                    {selectedMaterial.originalData.instituteId || "N/A"}
                  </Typography>
                </Grid>
                */}

                {/* Files */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Files
                  </Typography>
                </Grid>
                {selectedMaterial.originalData.files && Array.isArray(selectedMaterial.originalData.files) && selectedMaterial.originalData.files.length > 0 ? (
                  selectedMaterial.originalData.files.map((file, index) => (
                    <Grid item xs={12} key={index}>
                      <Box
                        sx={{
                          p: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <Avatar sx={{ bgcolor: "primary.main" }}>
                          {getFileIcon(
                            file.type?.includes("pdf")
                              ? "pdf"
                              : file.type?.includes("video")
                                ? "video"
                                : "file"
                          )}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {typeof file.name === "string" && (file.name.startsWith("http") || file.name.includes("/"))
                              ? `File ${index + 1}`
                              : file.name || `File ${index + 1}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {file.size ? `${(file.size / 1024).toFixed(2)} KB` : "N/A"} • {file.type || "Unknown type"}
                          </Typography>
                          {file.uploadedAt && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              Uploaded: {new Date(file.uploadedAt).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                        {file.url && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<OpenInNew />}
                            component="a"
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Document
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      No files available
                    </Typography>
                  </Grid>
                )}

                {/* Timestamps */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Timestamps
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {selectedMaterial.originalData.createdAt
                      ? new Date(selectedMaterial.originalData.createdAt).toLocaleString()
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Updated At
                  </Typography>
                  <Typography variant="body1">
                    {selectedMaterial.originalData.updatedAt
                      ? new Date(selectedMaterial.originalData.updatedAt).toLocaleString()
                      : "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setViewDialogOpen(false);
              setSelectedMaterial(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Toast
        open={!!toastMessage}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </Box>
  );
};

export default TeacherMaterials;
